<script setup lang="ts">
import { ref } from 'vue'
import type { AgentFindingDay } from '../../api/client'

// `canAck` = the viewer is a superuser acting as themselves (the server enforces the
// same rule); `busy` disables the controls while the card talks to the server.
const props = withDefaults(defineProps<{ day: AgentFindingDay; canAck?: boolean; busy?: boolean }>(),
  { canAck: false, busy: false })
const emit = defineEmits<{
  (e: 'acknowledge', day: AgentFindingDay, note: string, reference: string | null): void
  (e: 'withdraw', day: AgentFindingDay): void
}>()

const KNOWN = new Set(['HEALTHY', 'BENIGN_KNOWN', 'DEGRADED', 'INCIDENT'])

function badgeClass(status: string): string {
  return KNOWN.has(status) ? `badge-${status.toLowerCase().replace('_', '-')}` : 'badge-unknown'
}

function fmtDate(d: string): string {
  return new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB',
    { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
}

function fmtTime(iso: string | null): string {
  if (!iso) return '--'
  return new Date(iso).toLocaleTimeString('en-GB',
    { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' UK'
}

const note = ref('')
const reference = ref('')

function submit() {
  const n = note.value.trim()
  if (!n) return
  emit('acknowledge', props.day, n, reference.value.trim() || null)
}
</script>

<template>
  <div class="agent-day" :data-testid="`agent-day-${day.date}`">
    <div class="day-head">
      <span class="day-date">{{ fmtDate(day.date) }}</span>
      <span class="badge" :class="badgeClass(day.status)" data-testid="status-badge">{{ day.status }}</span>
      <span v-if="day.acknowledgement" class="badge badge-acked" data-testid="ack-badge">ACKNOWLEDGED</span>
    </div>
    <div class="day-meta">
      {{ day.affectedModule ?? '—' }} · {{ day.runs }} run{{ day.runs === 1 ? '' : 's' }} · last {{ fmtTime(day.lastRunUtc) }}
    </div>
    <div v-if="day.acknowledgement" class="ack" data-testid="agent-ack">
      <span>
        Acknowledged {{ fmtTime(day.acknowledgement.at) }}<template v-if="day.acknowledgement.byEmail"> by {{ day.acknowledgement.byEmail }}</template><template v-if="day.acknowledgement.note"> — {{ day.acknowledgement.note }}</template><template v-if="day.acknowledgement.reference"> ({{ day.acknowledgement.reference }})</template>
      </span>
      <button v-if="canAck" type="button" class="ack-btn" :disabled="busy" data-testid="agent-unack-btn"
        @click="emit('withdraw', day)">Withdraw</button>
    </div>
    <form v-else-if="canAck && day.status !== 'HEALTHY'" class="ack-form" data-testid="agent-ack-form" @submit.prevent="submit">
      <input v-model="note" type="text" maxlength="500" placeholder="Why this is handled (required)"
        :disabled="busy" data-testid="agent-ack-note">
      <input v-model="reference" type="text" maxlength="200" placeholder="PR / memory ref (optional)"
        :disabled="busy" data-testid="agent-ack-ref">
      <button type="submit" class="ack-btn" :disabled="busy || !note.trim()" data-testid="agent-ack-btn">Acknowledge</button>
    </form>
    <div v-if="day.detail" class="day-detail" data-testid="agent-detail">
      <p v-if="day.detail.rootCause"><strong>Root cause:</strong> {{ day.detail.rootCause }}</p>
      <ul v-if="day.detail.evidence.length" class="evidence">
        <li v-for="(e, i) in day.detail.evidence" :key="i">{{ e }}</li>
      </ul>
      <p v-if="day.detail.recommendedAction" class="action">
        <strong>Recommended action:</strong> {{ day.detail.recommendedAction }}
      </p>
      <p class="day-foot">
        confidence {{ day.detail.confidence ?? '—' }}<template v-if="day.detail.runbookRef"> · runbook {{ day.detail.runbookRef }}</template><template v-if="day.detail.benignMatch"> · benign match {{ day.detail.benignMatch }}</template>
      </p>
    </div>
  </div>
</template>

<style scoped>
.agent-day { padding: 0.5rem 0; }
.day-head { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.day-date { font-weight: 600; }
.day-meta { color: #9e9e9e; font-size: 0.9em; margin-top: 0.15rem; }
.badge { padding: 0.1rem 0.5rem; border-radius: 999px; font-size: 0.8em; font-weight: 700; letter-spacing: 0.02em; }
.badge-healthy { color: #4caf50; background: #4caf5019; }
.badge-benign-known, .badge-unknown { color: #9e9e9e; background: #9e9e9e22; }
.badge-degraded { color: #ff9800; background: #ff980022; }
.badge-incident { color: #f44336; background: #f4433622; }
.badge-acked { color: #93c5fd; background: #93c5fd22; }
.ack { margin-top: 0.35rem; color: #93c5fd; font-size: 0.9em; display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; overflow-wrap: anywhere; }
.ack-form { margin-top: 0.35rem; display: flex; gap: 0.4rem; flex-wrap: wrap; }
.ack-form input { flex: 1 1 12rem; min-width: 0; padding: 0.3rem 0.5rem; border-radius: 4px; border: 1px solid #ffffff2a; background: #ffffff0a; color: inherit; }
.ack-btn { padding: 0.3rem 0.7rem; border-radius: 4px; border: 1px solid #93c5fd66; background: #93c5fd1a; color: #93c5fd; cursor: pointer; }
.ack-btn:disabled { opacity: 0.5; cursor: default; }
.day-detail { margin-top: 0.5rem; overflow-wrap: anywhere; }
.day-detail p { margin: 0.35rem 0; }
.action { white-space: pre-line; }
.evidence { margin: 0.35rem 0; padding-left: 1.2rem; }
.evidence li { margin: 0.15rem 0; }
.day-foot { color: #9e9e9e; font-size: 0.85em; }
</style>
